import React, { useState } from 'react';
import { Flame, Lightbulb, Zap, Sparkles, CheckCircle, Star, Users, Globe } from 'lucide-react';
import PageLayout, { PageHero, PageSection, PageCTA } from '../components/common/PageLayout';
import SEOHead from '../components/common/SEOHead';
import { useLanguage } from '../i18n/LanguageContext';

const FX_GALLERY = [
  { url: 'https://customer-assets.emergentagent.com/job_aerial-performance/artifacts/xy9crazs_IMG_0678.jpeg', titleKey: 'concert' },
  { url: 'https://customer-assets.emergentagent.com/job_aerial-performance/artifacts/6lghk188_6D5BE1DD-A003-4661-8A21-A95E8DAF5F00.png', titleKey: 'corporate' },
  { url: 'https://customer-assets.emergentagent.com/job_aerial-performance/artifacts/2xztpazu_0CDB6C67-7F8E-4A0A-A127-873DB57E2172.png', titleKey: 'brand' },
  { url: 'https://customer-assets.emergentagent.com/job_aerial-performance/artifacts/wjkmdoh0_394EB39E-BAE8-427B-8173-080A499032F7.png', titleKey: 'wedding' },
];

const galleryTitles = {
  es: { concert: 'Concierto con efectos', corporate: 'Evento corporativo', brand: 'Activación de marca', wedding: 'Boda premium' },
  en: { concert: 'Concert with effects', corporate: 'Corporate event', brand: 'Brand activation', wedding: 'Premium wedding' },
};

const fxCategories = [
  { icon: Flame, key: 'coldPyro', color: 'from-orange-500 to-red-600' },
  { icon: Lightbulb, key: 'lighting', color: 'from-yellow-400 to-orange-500' },
  { icon: Zap, key: 'laser', color: 'from-purple-500 to-pink-600' },
  { icon: Sparkles, key: 'confetti', color: 'from-cyan-400 to-blue-500' },
];

const benefitIcons = [Star, Users, Globe, Zap];
const benefitKeys = ['safeImpact', 'fullCustom', 'turnkey', 'multiSensory'];

const EfectosEspecialesPage = () => {
  const [lightboxImage, setLightboxImage] = useState(null);
  const { language } = useLanguage();

  const content = {
    es: {
      title: "Efectos Especiales",
      titleHighlight: "para Eventos",
      subtitle: "Pirotecnia fría, iluminación, láser y confeti para crear experiencias multisensoriales inolvidables.",
      description: "Transformamos cualquier espacio en un escenario espectacular con tecnología de efectos especiales de última generación.",
      whyChoose: "¿Por qué elegir",
      whyChooseHighlight: "Nuestros FX",
      benefits: {
        safeImpact: { title: "Impacto Seguro", desc: "Efectos espectaculares sin riesgos. Pirotecnia fría certificada para uso en interiores." },
        fullCustom: { title: "100% Personalizable", desc: "Diseñamos cada efecto según tu marca, colores y visión creativa." },
        turnkey: { title: "Producción Integral", desc: "Nos encargamos de todo: diseño, instalación, operación y desmontaje." },
        multiSensory: { title: "Multisensorial", desc: "Combinamos luz, fuego, humo y movimiento para experiencias completas." },
      },
      categories: {
        coldPyro: { title: "Pirotecnia Fría", desc: "Fuentes de chispas, gerbs y efectos de fuego seguros para interiores. Sin humo, sin olor, máximo impacto visual." },
        lighting: { title: "Iluminación Escénica", desc: "Sistemas de iluminación arquitectónica, wash lights, moving heads y efectos de color sincronizados." },
        laser: { title: "Shows de Láser", desc: "Espectáculos de láser RGB de alta potencia sincronizados con música y coreografías programadas." },
        confetti: { title: "Confeti y CO2", desc: "Cañones de confeti, serpentinas, jets de CO2 y efectos de niebla para momentos climáticos." },
      },
      categoriesTitle: "Nuestras",
      categoriesTitleHighlight: "Especialidades",
      applications: "Aplicaciones",
      applicationsHighlight: "Perfectas",
      appList: [
        "Lanzamientos de productos y marcas",
        "Conciertos y festivales",
        "Eventos corporativos y galas",
        "Bodas y celebraciones privadas",
        "Inauguraciones y premieres",
        "Shows teatrales y espectáculos",
      ],
      gallery: "Galería de",
      galleryHighlight: "Experiencias",
      ctaTitle: "¿Listo para crear",
      ctaTitleHighlight: "magia visual",
      ctaDescription: "Nuestro equipo de expertos diseñará un show de efectos especiales único para tu evento.",
    },
    en: {
      title: "Special Effects",
      titleHighlight: "for Events",
      subtitle: "Cold pyrotechnics, lighting, laser and confetti to create unforgettable multisensory experiences.",
      description: "We transform any space into a spectacular stage with cutting-edge special effects technology.",
      whyChoose: "Why choose",
      whyChooseHighlight: "Our FX",
      benefits: {
        safeImpact: { title: "Safe Impact", desc: "Spectacular effects without risks. Certified cold pyrotechnics for indoor use." },
        fullCustom: { title: "100% Customizable", desc: "We design each effect according to your brand, colors and creative vision." },
        turnkey: { title: "Turnkey Production", desc: "We handle everything: design, installation, operation and dismantling." },
        multiSensory: { title: "Multisensory", desc: "We combine light, fire, smoke and movement for complete experiences." },
      },
      categories: {
        coldPyro: { title: "Cold Pyrotechnics", desc: "Spark fountains, gerbs and safe fire effects for indoors. No smoke, no smell, maximum visual impact." },
        lighting: { title: "Stage Lighting", desc: "Architectural lighting systems, wash lights, moving heads and synchronized color effects." },
        laser: { title: "Laser Shows", desc: "High-power RGB laser spectacles synchronized with music and programmed choreographies." },
        confetti: { title: "Confetti & CO2", desc: "Confetti cannons, streamers, CO2 jets and fog effects for climactic moments." },
      },
      categoriesTitle: "Our",
      categoriesTitleHighlight: "Specialties",
      applications: "Perfect",
      applicationsHighlight: "Applications",
      appList: [
        "Product and brand launches",
        "Concerts and festivals",
        "Corporate events and galas",
        "Weddings and private celebrations",
        "Openings and premieres",
        "Theatrical shows and spectacles",
      ],
      gallery: "Experience",
      galleryHighlight: "Gallery",
      ctaTitle: "Ready to create",
      ctaTitleHighlight: "visual magic",
      ctaDescription: "Our team of experts will design a unique special effects show for your event.",
    }
  };

  const c = content[language] || content.es;

  return (
    <PageLayout>
      <SEOHead 
        title={language === 'es' ? "Efectos Especiales para Eventos | Pirotecnia, Láser, Iluminación | NexAir" : "Special Effects for Events | Pyrotechnics, Laser, Lighting | NexAir"}
        description={language === 'es' ? "Servicios profesionales de efectos especiales para eventos en México. Pirotecnia fría, shows de láser, iluminación escénica y confeti. Creamos experiencias multisensoriales inolvidables." : "Professional special effects services for events in Mexico. Cold pyrotechnics, laser shows, stage lighting and confetti. We create unforgettable multisensory experiences."}
        keywords={language === 'es' ? "efectos especiales eventos, pirotecnia fria, show laser mexico, iluminacion eventos, confeti eventos, fx para fiestas" : "special effects events, cold pyrotechnics, laser show mexico, event lighting, confetti events, fx for parties"}
        canonicalUrl="https://nexair.com.mx/efectos-especiales"
      />
      
      <PageHero 
        image="https://customer-assets.emergentagent.com/job_aerial-performance/artifacts/xy9crazs_IMG_0678.jpeg"
        title={c.title}
        titleHighlight={c.titleHighlight}
        subtitle={c.subtitle}
        description={c.description}
        gradient="from-purple-600/30 to-pink-600/30"
      />
      
      {/* Benefits Section */}
      <PageSection className="bg-[#030810]">
        <h2 className="text-3xl md:text-4xl font-bold text-white mb-12 text-center font-heading">
          {c.whyChoose} <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-pink-500">{c.whyChooseHighlight}</span>?
        </h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {benefitKeys.map((key, index) => {
            const Icon = benefitIcons[index];
            return (
              <div key={index} className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 hover:border-purple-500/50 transition-all">
                <div className="w-14 h-14 rounded-xl bg-gradient-to-r from-purple-500/20 to-pink-500/20 flex items-center justify-center mb-4">
                  <Icon className="w-7 h-7 text-purple-400" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2 font-heading">{c.benefits[key].title}</h3>
                <p className="text-gray-400">{c.benefits[key].desc}</p>
              </div>
            );
          })}
        </div>
      </PageSection>
      
      {/* FX Categories Section */}
      <PageSection className="bg-[#050a15]">
        <h2 className="text-3xl md:text-4xl font-bold text-white mb-12 text-center font-heading">
          {c.categoriesTitle} <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-pink-500">{c.categoriesTitleHighlight}</span>
        </h2>
        <div className="grid md:grid-cols-2 gap-8">
          {fxCategories.map((cat, index) => {
            const Icon = cat.icon;
            return (
              <div key={index} className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8 hover:border-purple-500/30 transition-all group">
                <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${cat.color} flex items-center justify-center mb-6 group-hover:scale-110 transition-transform`}>
                  <Icon className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-3 font-heading">{c.categories[cat.key].title}</h3>
                <p className="text-gray-400 leading-relaxed">{c.categories[cat.key].desc}</p>
              </div>
            );
          })}
        </div>
      </PageSection>

      {/* Applications */}
      <PageSection className="bg-[#030810]">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-8 font-heading">
              {c.applications} <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-pink-500">{c.applicationsHighlight}</span>
            </h2>
            <ul className="space-y-4">
              {c.appList.map((item, index) => (
                <li key={index} className="flex items-center gap-4 text-lg text-gray-300">
                  <CheckCircle className="w-6 h-6 text-purple-400 flex-shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
          </div>
          <div className="relative">
            <img 
              src="https://customer-assets.emergentagent.com/job_aerial-performance/artifacts/2xztpazu_0CDB6C67-7F8E-4A0A-A127-873DB57E2172.png"
              alt="Special Effects"
              className="rounded-2xl shadow-2xl"
            />
            <div className="absolute inset-0 rounded-2xl bg-gradient-to-t from-purple-600/20 to-transparent" />
          </div>
        </div>
      </PageSection>
      
      {/* Gallery */}
      <PageSection className="bg-[#050a15]">
        <h2 className="text-3xl md:text-4xl font-bold text-white mb-12 text-center font-heading">
          {c.gallery} <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-pink-500">{c.galleryHighlight}</span>
        </h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          {FX_GALLERY.map((img, index) => (
            <div 
              key={index}
              className="relative aspect-square rounded-xl overflow-hidden cursor-pointer group"
              onClick={() => setLightboxImage(img.url)}
            >
              <img 
                src={img.url}
                alt={galleryTitles[language]?.[img.titleKey] || img.titleKey}
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-4">
                <span className="text-white font-medium">{galleryTitles[language]?.[img.titleKey] || img.titleKey}</span>
              </div>
            </div>
          ))}
        </div>
      </PageSection>
      
      {/* Lightbox */}
      {lightboxImage && (
        <div 
          className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4"
          onClick={() => setLightboxImage(null)}
        >
          <img 
            src={lightboxImage}
            alt="Gallery"
            className="max-w-full max-h-[90vh] object-contain rounded-lg"
          />
          <button 
            className="absolute top-6 right-6 text-white/70 hover:text-white text-4xl"
            onClick={() => setLightboxImage(null)}
          >
            ×
          </button>
        </div>
      )}
      
      <PageCTA 
        title={c.ctaTitle}
        titleHighlight={c.ctaTitleHighlight}
        description={c.ctaDescription}
        gradient="from-purple-600 to-pink-600"
      />
    </PageLayout>
  );
};

export default EfectosEspecialesPage;
